import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import MuiDrawer from '@material-ui/core/Drawer';
import PropTypes from 'prop-types';
import { dimensions } from 'theme/constants';

const WINDOW_EVENTS = ['scroll', 'resize'];

export const NAV_HEIGHT = parseInt(`${dimensions.navHeight}`, 10);

const Drawer = ({
  open,
  containerStyle = {},
  zIndex = 110,
  top = NAV_HEIGHT,
  right = 0,
  width = '350px',
  maxWidth,
  alignLeft,
  children,
  absolute,
  overflow = 'auto',
  className,
  variant = 'persistent',
  onClose,
  backdropProps,
}) => {
  const [isRequestingAF, setIsRequestingAF] = useState(false);

  const drawerEl = useRef(null);
  const anchor = alignLeft ? 'left' : 'right';
  const position = absolute ? 'absolute' : 'fixed';

  const height = absolute ? 'auto' : `calc(100vh - ${top}px)`;

  const computedContainerStyle = useMemo(
    () => ({
      style: {
        zIndex,
        top,
        right,
        maxWidth,
        width: maxWidth ? 'auto' : width,
        position,
        height,
        overflow,
        ...containerStyle,
      },
    }),
    [
      zIndex,
      top,
      right,
      maxWidth,
      width,
      position,
      height,
      overflow,
      containerStyle,
    ]
  );

  const scrollDrawerTop = useCallback(() => {
    setIsRequestingAF(false);

    const scrollY = window.scrollY || window.pageYOffset;
    const newTop = Math.max(0, top - scrollY);
    const newHeight = window.innerHeight - newTop;

    if (drawerEl.current?.firstElementChild) {
      drawerEl.current.firstElementChild.style.top = `${newTop}px`;
      drawerEl.current.firstElementChild.style.height = `${newHeight}px`;
    }
  }, [top]);

  const onWindowEvent = useCallback(() => {
    if (absolute) return;

    if (!isRequestingAF) {
      requestAnimationFrame(scrollDrawerTop);
      setIsRequestingAF(true);
    }
  }, [absolute, isRequestingAF, scrollDrawerTop]);

  useEffect(() => {
    if (open) {
      WINDOW_EVENTS.forEach(event =>
        window.addEventListener(event, onWindowEvent)
      );
    }

    return () => {
      WINDOW_EVENTS.forEach(event =>
        window.removeEventListener(event, onWindowEvent)
      );
    };
  }, [open, onWindowEvent]);

  return (
    <MuiDrawer
      ref={drawerEl}
      transitionDuration={window.testEnv ? 0 : 500}
      variant={variant}
      anchor={anchor}
      open={open}
      onClose={onClose}
      PaperProps={computedContainerStyle}
      BackdropProps={backdropProps}
      className={className}
    >
      {children}
    </MuiDrawer>
  );
};

Drawer.propTypes = {
  open: PropTypes.bool.isRequired,
  containerStyle: PropTypes.object,
  zIndex: PropTypes.number,
  top: PropTypes.number,
  right: PropTypes.number,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  overflow: PropTypes.string,
  alignLeft: PropTypes.bool,
  children: PropTypes.node,
  absolute: PropTypes.bool,
  variant: PropTypes.oneOf(['persistent', 'temporary', 'permanent']),
  onClose: PropTypes.func,
  backdropProps: PropTypes.object,
};

export default memo(Drawer);
